import { IGeoCoords, Location, Sublocation } from '@uzurv/clara';
import { AutoCompleteResult } from '@uzurv/core';
import { createReducer, on } from '@ngrx/store';
import * as LocationManagerActions from './location-manager.actions';

export interface LocationManagerState {
  searchString: string;
  loadingSearchResults: boolean;
  autoCompleteResults: Array<AutoCompleteResult>;
  selectedLocationAutocompleteString: string;
  loadingLocation: boolean;
  loadedLocation: Location;
  mapLoaded: boolean;
  mapCenterCoords: IGeoCoords;
  offsetLocationMode: boolean;
  hoveredLocation: Location | Sublocation;
  selectedLocation: Location;
  addingNewLocation: boolean;
  newLocationName: string;
  sendingNewLocation: boolean;
  deletingSublocation: Sublocation;
  userLocation: IGeoCoords;
  geocodedOffsetLocation: Location;
}

export const initialState: LocationManagerState = {
  searchString: '',
  loadingSearchResults: false,
  autoCompleteResults: [],
  selectedLocationAutocompleteString: '',
  loadingLocation: false,
  loadedLocation: null,
  mapLoaded: false,
  mapCenterCoords: null,
  offsetLocationMode: false,
  hoveredLocation: null,
  selectedLocation: null,
  addingNewLocation: false,
  newLocationName: '',
  sendingNewLocation: false,
  deletingSublocation: null,
  userLocation: null,
  geocodedOffsetLocation: null
};

export const locationManagerReducer = createReducer(
  initialState,

  on(LocationManagerActions.Reset, state => ({
    ...initialState,
    userLocation: state.userLocation
  })),

  on(LocationManagerActions.Search, (state, { searchString }) => ({
    ...state,
    autoCompleteResults: state.autoCompleteResults,
    loadingSearchResults: true,
    searchString: searchString
  })),

  on(LocationManagerActions.SearchResultsLoaded, (state, { results }) => {
    let autoCompleteResults = results;
    if (autoCompleteResults.length === 0) {
      autoCompleteResults = state.autoCompleteResults;
    }
    return {
      ...state,
      loadingSearchResults: false,
      autoCompleteResults: autoCompleteResults
    };
  }),

  on(LocationManagerActions.LoadSearchResultsFailed, state => ({
    ...state,
    loadingSearchResults: false
  })),

  on(LocationManagerActions.ClearSearch, state => ({
    ...initialState,
    userLocation: state.userLocation,
    mapCenterCoords: state.userLocation,
    loadedLocation: null,
    searchString: '',
    autoCompleteResults: []
  })),

  on(LocationManagerActions.SearchResultSelected, (state, { locationLabel }) => ({
    ...state,
    loadingLocation: true,
    autoCompleteResults: [],
    selectedLocationAutocompleteString: locationLabel
  })),

  on(LocationManagerActions.LocationLoaded, (state, { location }) => {
    const selectedLocation = new Location();
    Object.assign(selectedLocation, location);
    selectedLocation.sublocations = null;
    return {
      ...state,
      loadingLocation: false,
      loadedLocation: location,
      selectedLocation: selectedLocation,
      mapCenterCoords: {
        lat: location.latitude,
        lng: location.longitude
      }
    };
  }),

  on(LocationManagerActions.LoadLocationFailed, state => ({
    ...state,
    loadingLocation: false
  })),

  on(LocationManagerActions.MapLoaded, state => ({
    ...state,
    mapLoaded: true
  })),

  on(LocationManagerActions.UseOffsetLocation, (state, { useOffset }) => ({
    ...state,
    selectedLocation: null,
    offsetLocationMode: useOffset
  })),

  on(LocationManagerActions.LocationMouseover, (state, { location }) => ({
    ...state,
    hoveredLocation: location
  })),

  on(LocationManagerActions.LocationMouseout, state => ({
    ...state,
    hoveredLocation: null
  })),

  on(LocationManagerActions.LocationSelected, (state, { location }) => ({
    ...state,
    offsetLocationMode: false,
    selectedLocation: location
  })),

  on(LocationManagerActions.AddNewLocation, state => ({
    ...state,
    offsetLocationMode: false,
    addingNewLocation: true
  })),

  on(LocationManagerActions.CancelAddNewLocation, state => ({
    ...state,
    addingNewLocation: false
  })),

  on(LocationManagerActions.MoveMap, (state, { coords }) => ({
    ...state,
    mapCenterCoords: coords
  })),

  on(LocationManagerActions.SetNewLocationName, (state, { name }) => ({
    ...state,
    newLocationName: name
  })),

  on(LocationManagerActions.SubmitNewLocation, state => ({
    ...state,
    sendingNewLocation: true
  })),

  on(LocationManagerActions.NewLocationInvalid, state => ({
    ...state,
    sendingNewLocation: false
  })),

  on(LocationManagerActions.NewLocationAccepted, (state, { sublocation }) => {
    const updatedLocation = new Location();
    Object.assign(updatedLocation, state.loadedLocation);
    if (updatedLocation.sublocations === null) {
      updatedLocation.sublocations = [];
    }
    updatedLocation.sublocations.push(sublocation);
    updatedLocation.sublocations.sort((a, b) => a.name.localeCompare(b.name));
    const updatedSelectedLocation = new Location();
    Object.assign(updatedSelectedLocation, updatedLocation);
    updatedSelectedLocation.sublocations = [sublocation];
    return {
      ...state,
      addingNewLocation: false,
      sendingNewLocation: false,
      newLocationName: '',
      loadedLocation: updatedLocation,
      selectedLocation: updatedSelectedLocation
    };
  }),

  on(LocationManagerActions.SubmitNewLocationFailed, state => ({
    ...state,
    sendingNewLocation: false,
    addingNewLocation: false
  })),

  on(LocationManagerActions.DeleteSublocation, (state, { sublocation }) => ({
    ...state,
    offsetLocationMode: false,
    deletingSublocation: sublocation
  })),

  on(LocationManagerActions.DeleteSublocationSuccess, state => {
    const locationWithDeletedSublocation = new Location();
    Object.assign(locationWithDeletedSublocation, state.loadedLocation);
    locationWithDeletedSublocation.sublocations.splice(
      locationWithDeletedSublocation.sublocations.findIndex(sublocation => sublocation.id === state.deletingSublocation.id),
      1
    );
    return {
      ...state,
      deletingSublocation: null,
      loadedLocation: locationWithDeletedSublocation
    };
  }),

  on(LocationManagerActions.DeleteSublocationFailed, state => ({
    ...state,
    deletingSublocation: null
  })),

  on(LocationManagerActions.GetUserLocationSuccess, (state, { coordinates }) => ({
    ...state,
    userLocation: coordinates
  })),

  on(LocationManagerActions.GeocodeOffsetLocationSuccess, (state, { location }) => ({
    ...state,
    geocodedOffsetLocation: location
  })),

  on(LocationManagerActions.RestoreSnapshot, (_state, { state }) => state)
);
